/**
 * Truncates sanitized rich-text HTML to `maxLength` characters of visible
 * text, preserving markup (links, formatting) up to the cut point and
 * appending an ellipsis. Expects input already passed through
 * normalizeRichTextHtml.
 */
export const truncateRichTextHtml = (
  html: string,
  maxLength: number,
): { html: string; isTruncated: boolean } => {
  if (!html || typeof DOMParser === "undefined") {
    return { html, isTruncated: false };
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  const body = doc.body;
  if ((body.textContent ?? "").length <= maxLength) {
    return { html, isTruncated: false };
  }

  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  let remaining = maxLength;
  let node = walker.nextNode() as Text | null;

  while (node) {
    const length = node.data.length;
    if (length >= remaining) {
      node.data = node.data.slice(0, remaining).trimEnd() + "…";
      break;
    }
    remaining -= length;
    node = walker.nextNode() as Text | null;
  }

  // Drop everything after the cut point.
  let current: Node | null = node;
  while (current && current !== body) {
    while (current.nextSibling) current.nextSibling.remove();
    current = current.parentNode;
  }

  return { html: body.innerHTML.trim(), isTruncated: true };
};
